import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  List,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useRef, useState } from 'react'
import { ViewPanel, ViewPanelHeader } from '../../../components/view-panel'
import { useQuery, useReactiveVar } from '@apollo/client'
import { ApiKeyType, GetApiKeyDocument } from '../../../generated/graphql'
import { reactiveApiKeyViewPanelVar } from './api-keys-list'
import { useTranslation } from 'react-i18next'
import { LockSmallIcon, SectionHeader } from '@sitoo/mui-components'
import { ListItemSkeleton } from '../../../components/list-item-skeleton'
import { useLocalizedDate } from '../../../hooks/localized-date'
import { EditApiKeyDialog } from './edit-api-key-dialog'
import { enqueueSnackbar } from 'notistack'
import { ResetApiKeyDialog } from './reset-api-key-dialog'

type ApiKeyViewPanelProps = {
  isOpen: boolean
  onClose: () => void
}

export const ApiKeyViewPanel = (props: ApiKeyViewPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const viewPanelState = useReactiveVar(reactiveApiKeyViewPanelVar)
  const { data, loading } = useQuery(GetApiKeyDocument, {
    variables: { apiKeyId: viewPanelState.id },
    skip: !viewPanelState.id,
    onError: (error) => {
      enqueueSnackbar(error.message, {
        variant: 'error',
      })
    },
    fetchPolicy: 'cache-and-network',
  })
  const { t } = useTranslation(['api_keys', 'shared'])
  const { formatDate } = useLocalizedDate()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

  const apiKey = data?.apiKey || {
    id: 0,
    name: '',
    username: '',
    type: ApiKeyType.Normal,
    enabled: false,
    ip_restrictions: [],
    last_used: null,
  }

  const isPasswordResettable = apiKey.enabled && apiKey.type !== ApiKeyType.Pos

  return (
    <ViewPanel open={props.isOpen} ref={panelRef}>
      <ViewPanelHeader
        isLoading={loading}
        title={apiKey.username}
        section={t('api_keys:page_title')}
        sectionIcon={<LockSmallIcon />}
        onClose={props.onClose}
        hideActionsSkeleton
        actions={
          <Button
            color="secondary"
            onClick={() => {
              setIsEditDialogOpen(true)
            }}
            disabled={loading}
            size="small"
            aria-label={t('api_keys:edit_api_key')}
          >
            {t('shared:action.edit')}
          </Button>
        }
        tags={
          <Chip
            component="span"
            label={
              apiKey.enabled ? t('api_keys:enabled') : t('api_keys:disabled')
            }
            variant={apiKey.enabled ? 'outlined' : 'filled'}
            color={apiKey.enabled ? 'green' : 'red'}
          />
        }
      />
      <Accordion defaultExpanded className="MuiAccordionRoot">
        <AccordionSummary aria-controls="information">
          <SectionHeader disablePadding>
            {t('shared:label.details')}
          </SectionHeader>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List>
            <ListItemSkeleton
              isLoading={loading}
              divider
              childrenSkeleton
              hasSecondaryText
            >
              <ListItemText
                primary={t('api_keys:name')}
                secondary={apiKey.name}
              />
            </ListItemSkeleton>
            <ListItemSkeleton
              isLoading={loading}
              divider
              childrenSkeleton
              secondaryAction={
                <Typography variant="body02">
                  {apiKey.enabled
                    ? t('api_keys:enabled')
                    : t('api_keys:disabled')}
                </Typography>
              }
            >
              <ListItemText primary={t('api_keys:status')} />
            </ListItemSkeleton>
            <ListItemSkeleton
              isLoading={loading}
              divider
              childrenSkeleton
              secondaryAction={
                <Typography variant="body02">
                  {t(`api_keys:type_${apiKey.type}`)}
                </Typography>
              }
            >
              <ListItemText primary={t('api_keys:type')} />
            </ListItemSkeleton>
            {apiKey.ip_restrictions.length > 0 && (
              <ListItemSkeleton isLoading={loading} divider childrenSkeleton>
                <ListItemText
                  primary={t('api_keys:ip_restrictions')}
                  secondary={
                    <Stack
                      component="span"
                      alignItems="flex-start"
                      sx={{
                        mt: 0.5,
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      {apiKey.ip_restrictions?.map((ip) => (
                        <Chip
                          label={ip}
                          key={ip}
                          variant="outlined"
                          component="span"
                          size="small"
                        />
                      ))}
                    </Stack>
                  }
                />
              </ListItemSkeleton>
            )}
            {apiKey.last_used && (
              <ListItemSkeleton
                isLoading={loading}
                divider
                childrenSkeleton
                secondaryAction={
                  <Typography variant="body02">
                    {formatDate(apiKey.last_used)}
                  </Typography>
                }
              >
                <ListItemText primary={t('api_keys:last_used')} />
              </ListItemSkeleton>
            )}
            <ListItemSkeleton
              hasSecondaryText
              childrenSkeleton
              isLoading={loading}
              secondaryAction={
                isPasswordResettable ? (
                  <Button
                    fullWidth
                    color="secondary"
                    onClick={() => {
                      setIsResetDialogOpen(true)
                    }}
                    disabled={!apiKey.enabled || loading}
                    size="small"
                  >
                    {t('shared:action.reset')}
                  </Button>
                ) : null
              }
            >
              <ListItemText
                primary={t('shared:label.password')}
                secondary={
                  !isPasswordResettable && (
                    <Typography variant="caption" color="red">
                      {apiKey.type === ApiKeyType.Pos
                        ? t('api_keys:reset_password_pos_message')
                        : !apiKey.enabled
                          ? t('api_keys:reset_password_disabled_message')
                          : null}
                    </Typography>
                  )
                }
              />
            </ListItemSkeleton>
          </List>
        </AccordionDetails>
      </Accordion>
      <EditApiKeyDialog
        apiKey={apiKey}
        onClose={() => {
          setIsEditDialogOpen(false)
        }}
        open={isEditDialogOpen}
      />
      <ResetApiKeyDialog
        open={isResetDialogOpen}
        onClose={() => {
          setIsResetDialogOpen(false)
        }}
        apiKeyId={apiKey.id}
        name={apiKey.name}
      />
    </ViewPanel>
  )
}
